import { apiClient } from './apiClient';
import { API_CONFIG } from '../config/api';
import { logInfo, logError } from './logtail';

// Define UserSearchResult interface locally to avoid circular imports
export interface UserSearchResult {
  id: string;
  userId: string;
  email: string;
  gdriveEmail?: string;
  storageProvider?: string;
  linkedProviders?: string[];
  defaultStorageProvider?: string;

  effectiveTier?: string;
  isPremium: boolean;
  subscriptionTier?: string;

  premiumUntil?: string;
  activePromoCode?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  firstSeenAt?: string;
  stripeCustomerId?: string;

  gdriveRootId?: string;
  gdriveRootLink?: string;
  firebaseUid?: string;
}

export type ShareStatus = 'active' | 'inactive' | 'pending';

export interface TrackShare {
  trackId: string;
  ownerUserId: string;
  collaboratorUserId: string;
  role: 'viewer' | 'editor' | 'musician' | 'sound_engineer' | 'content_maker';
  invitationId?: string;
  status?: ShareStatus;
  inactiveReason?: string;
  createdAt: string;
  updatedAt: string;
  collaborator?: UserSearchResult;
  owner?: UserSearchResult;
}

export interface SyncCollaborationSummary {
  sharesActivated: number;
  sharesDeactivated: number;
  aclErrors?: string[];
  filesUpdated?: boolean;
}

export interface ShareWithTrack {
  share: TrackShare;
  track?: any; // Track type
}

export const createShare = async (trackId: string, collaboratorUserId: string, role: 'viewer' | 'editor' | 'musician' | 'sound_engineer' | 'content_maker'): Promise<TrackShare> => {
  try {
    const response = await apiClient.post(API_CONFIG.ENDPOINTS.TRACK_SHARES(trackId), {
      collaboratorUserId,
      role,
    });
    logInfo('Share created', { trackId, collaboratorUserId, role });
    return response.data.share;
  } catch (error: any) {
    logError('Failed to create share', { trackId, collaboratorUserId, error });
    throw error;
  }
};

export const listShares = async (trackId: string): Promise<TrackShare[]> => {
  try {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.TRACK_SHARES(trackId));
    logInfo('Shares listed', { trackId, count: response.data.length });
    
    // Transform the response data - extract the share objects and add embedded user data
    const transformedShares: TrackShare[] = response.data.map((item: any) => ({
      ...item.share,
      collaborator: item.collaborator,
      owner: item.owner
    }));
    
    return transformedShares;
  } catch (error) {
    logError('Failed to list shares', { trackId, error });
    throw error;
  }
};

export const changeShareRole = async (trackId: string, collaboratorUserId: string, role: 'viewer' | 'editor' | 'musician' | 'sound_engineer' | 'content_maker'): Promise<void> => {
  try {
    await apiClient.patch(API_CONFIG.ENDPOINTS.TRACK_SHARE_COLLABORATOR(trackId, collaboratorUserId), { role });
    logInfo('Share role changed', { trackId, collaboratorUserId, role });
  } catch (error) {
    logError('Failed to change share role', { trackId, collaboratorUserId, error });
    throw error;
  }
};

export const revokeShare = async (trackId: string, collaboratorUserId: string): Promise<void> => {
  try {
    await apiClient.delete(API_CONFIG.ENDPOINTS.TRACK_SHARE_COLLABORATOR(trackId, collaboratorUserId));
    logInfo('Share revoked', { trackId, collaboratorUserId });
  } catch (error) {
    logError('Failed to revoke share', { trackId, collaboratorUserId });
    throw error;
  }
};

export const listSharedWithMe = async (): Promise<ShareWithTrack[]> => {
  try {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.SHARED_WITH_ME);
    logInfo('Shared with me tracks loaded', { count: response.data.length });
    return response.data;
  } catch (error) {
    logError('Failed to load shared with me tracks', { error });
    throw error;
  }
};

export const syncTrackCollaboration = async (trackId: string): Promise<SyncCollaborationSummary> => {
  try {
    const response = await apiClient.post(API_CONFIG.ENDPOINTS.TRACK_SYNC_COLLABORATION(trackId));
    logInfo('Track collaboration synced', { trackId, summary: response.data.summary });
    return response.data.summary;
  } catch (error) {
    logError('Failed to sync track collaboration', { trackId, error });
    throw error;
  }
};

